export interface ApiError {
  error: string;
}

export type ApiResult<T> =
  | { ok: true; data: T }
  | {
      ok: false;
      status: number;
      error: string;
      /** Seconds until a rate-limited call may be made again. */
      retryAfter?: number;
      /** The rest of the error body, for pages that act on more than the message. */
      body?: Record<string, unknown>;
    };

async function request<T>(path: string, init?: RequestInit): Promise<ApiResult<T>> {
  try {
    const res = await fetch(path, {
      ...init,
      credentials: "same-origin",
      headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
    });
    const text = await res.text();
    const data = text ? JSON.parse(text) : {};
    if (!res.ok) {
      const header = Number(res.headers.get("retry-after"));
      const retryAfter = Number.isFinite(data.retryAfter) ? Number(data.retryAfter) : header > 0 ? header : undefined;
      return { ok: false, status: res.status, error: data.error ?? res.statusText, retryAfter, body: data };
    }
    return { ok: true, data: data as T };
  } catch (err) {
    return { ok: false, status: 0, error: (err as Error).message };
  }
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};
